import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ShoppingCart, ArrowLeft, Minus, Plus, X, ZoomIn, Truck, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { formatCLP, imgSrc, addToCart, youtubeEmbed } from "@/lib/tienda";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ProductoDetalle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    axios.get(`${API}/api/tienda/productos/${slug}`)
      .then((r) => setP(r.data))
      .catch(() => { toast.error("Producto no encontrado"); navigate("/productos"); });
  }, [slug, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setZoom(false);
      const n = (p?.imagenes || []).length;
      if (n > 1 && e.key === "ArrowRight") setImg((i) => (i + 1) % n);
      if (n > 1 && e.key === "ArrowLeft") setImg((i) => (i - 1 + n) % n);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [p]);

  if (!p) return <div className="min-h-[60vh] bg-[#F8FAFC] flex items-center justify-center text-gray-400">Cargando…</div>;

  const imgs = p.imagenes || [];
  const video = youtubeEmbed(p.video_url);
  const anterior = (e) => { e.stopPropagation(); setImg((img - 1 + imgs.length) % imgs.length); };
  const siguiente = (e) => { e.stopPropagation(); setImg((img + 1) % imgs.length); };

  const agregar = (irACheckout) => {
    if (!p.disponible) return toast.error("Producto agotado");
    addToCart(p, qty);
    toast.success(`${p.nombre} agregado al carrito`);
    if (irACheckout) navigate("/productos/checkout");
  };

  return (
    <div className="bg-[#F8FAFC] min-h-[70vh]">
      {/* lightbox: imagen ampliada */}
      {zoom && imgs[img] && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setZoom(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white"><X className="h-8 w-8" /></button>
          <img src={imgSrc(imgs[img])} alt={p.nombre}
            className="max-h-[85vh] max-w-[92vw] object-contain rounded-lg" />
          {imgs.length > 1 && (
            <>
              <button onClick={anterior}
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/30 text-white p-2.5">
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button onClick={siguiente}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/15 hover:bg-white/30 text-white p-2.5">
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
          {imgs.length > 1 && (
            <div className="absolute bottom-5 inset-x-0 flex justify-center gap-2" onClick={(e) => e.stopPropagation()}>
              {imgs.map((id, i) => (
                <button key={id} onClick={() => setImg(i)}
                  className={`h-2.5 w-2.5 rounded-full ${i === img ? "bg-white" : "bg-white/40"}`} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
        <Link to="/productos" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#384091] mb-5">
          <ArrowLeft className="h-4 w-4" /> Volver a la tienda
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* galería */}
          <div>
            <div className="aspect-square max-h-[62vh] w-full rounded-3xl bg-white border border-gray-100 overflow-hidden relative cursor-zoom-in group"
              onClick={() => imgs[img] && setZoom(true)}>
              {imgs[img] ? (
                <>
                  <img src={imgSrc(imgs[img])} alt={p.nombre} className="w-full h-full object-contain p-3" />
                  <span className="absolute bottom-3 right-3 rounded-full bg-black/50 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="h-4 w-4" />
                  </span>
                  {imgs.length > 1 && (
                    <>
                      <button onClick={anterior}
                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 border border-gray-200 shadow p-2 text-gray-600 hover:text-[#384091]">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button onClick={siguiente}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 border border-gray-200 shadow p-2 text-gray-600 hover:text-[#384091]">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      <span className="absolute bottom-3 left-3 rounded-full bg-black/40 text-white text-[11px] px-2 py-0.5">
                        {img + 1} / {imgs.length}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-300">Sin imagen</div>
              )}
            </div>
            {(imgs.length > 1 || video) && (
              <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                {imgs.map((id, i) => (
                  <button key={id} onClick={() => setImg(i)}
                    className={`h-18 w-18 h-[72px] w-[72px] rounded-xl overflow-hidden border-2 flex-shrink-0 ${i === img ? "border-[#0FB4B7]" : "border-transparent"}`}>
                    <img src={imgSrc(id)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            {video && (
              <div className="mt-4 aspect-video rounded-2xl overflow-hidden border border-gray-100">
                <iframe src={video} title="Video del producto" className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen />
              </div>
            )}
          </div>

          {/* info */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#0FB4B7]">{p.categoria}</p>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#111827] mt-1">{p.nombre}</h1>
            {p.sku && <p className="text-xs text-gray-400 mt-1">SKU {p.sku}</p>}

            <div className="mt-4 flex items-baseline gap-3">
              <span className="text-3xl font-bold text-[#384091]">{formatCLP(p.precio_final)}</span>
              {p.precio_oferta > 0 && p.precio_oferta < p.precio && (
                <span className="text-lg text-gray-400 line-through">{formatCLP(p.precio)}</span>
              )}
            </div>

            {p.descripcion_corta && <p className="mt-3 text-gray-600">{p.descripcion_corta}</p>}

            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center rounded-xl border border-gray-200 bg-white">
                <button className="p-3" onClick={() => setQty(Math.max(1, qty - 1))}><Minus className="h-4 w-4" /></button>
                <span className="w-8 text-center font-semibold">{qty}</span>
                <button className="p-3" onClick={() => setQty(Math.min(p.stock || 99, qty + 1))}><Plus className="h-4 w-4" /></button>
              </div>
              <span className="text-xs text-gray-400">{p.disponible ? `${p.stock} disponibles` : "Agotado"}</span>
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button className="h-13 py-3.5 bg-[#0FB4B7] hover:bg-[#0D999C] text-white font-semibold"
                disabled={!p.disponible} onClick={() => agregar(false)}>
                <ShoppingCart className="h-5 w-5 mr-2" /> Agregar al carrito
              </Button>
              <Button className="h-13 py-3.5 bg-[#384091] hover:bg-[#2D3375] text-white font-semibold"
                disabled={!p.disponible} onClick={() => agregar(true)}>
                Comprar ahora
              </Button>
            </div>

            <div className="mt-6 space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#0FB4B7]" /> Despacho a todo Chile (costo estimado en el checkout) o retiro en tienda.</p>
              <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#0FB4B7]" /> Respaldado por la red técnica Odontofix.</p>
            </div>

            {p.descripcion && (
              <div className="mt-8 border-t pt-6">
                <h2 className="font-heading font-bold text-[#111827] mb-2">Descripción</h2>
                <p className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{p.descripcion}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
